type BaseTranslationKeys = {
  checkin: string;
  checkout: string;
  guests: string;
  book: string;
  officialSite: string;
  otherSites: string;
  unavailable: string;
  noRateAvailable: string;
  missingToken: string;
  unknownError: string;
  invalidToken: string;
  serviceUnavailable: (status: number) => string;
  noData: string;
  decrease: string;
  increase: string;
  nightsSummary: (nights: number, guests: number) => string;
};

type RoomTranslationKeys = {
  /** Mini room widget: prefix label above price, e.g. "From 120€" */
  priceFrom: string;
  /** Mini room widget: shown when no room id is configured. */
  missingRoomConfig: string;
};

export type TranslationKeys = BaseTranslationKeys & RoomTranslationKeys;

/**
 * Per-locale strings for the mini room price widget. Kept in a separate
 * map to avoid touching every existing locale dictionary; merged on top
 * by `t()`. Fallback values reuse the French strings.
 */
const ROOM_WIDGET_STRINGS: Record<string, RoomTranslationKeys> = {
  fr: { priceFrom: 'À partir de', missingRoomConfig: 'Identifiant chambre manquant' },
  en: { priceFrom: 'From', missingRoomConfig: 'Missing room identifier' },
  de: { priceFrom: 'Ab', missingRoomConfig: 'Zimmer-ID fehlt' },
  es: { priceFrom: 'Desde', missingRoomConfig: 'Falta el identificador de la habitación' },
  it: { priceFrom: 'Da', missingRoomConfig: 'Identificativo camera mancante' },
  nl: { priceFrom: 'Vanaf', missingRoomConfig: 'Kamer-ID ontbreekt' },
  pt: { priceFrom: 'Desde', missingRoomConfig: 'Identificador de quarto em falta' },
  pl: { priceFrom: 'Od', missingRoomConfig: 'Brak identyfikatora pokoju' },
  cs: { priceFrom: 'Od', missingRoomConfig: 'Chybí identifikátor pokoje' },
  sk: { priceFrom: 'Od', missingRoomConfig: 'Chýba identifikátor izby' },
  hu: { priceFrom: 'Ettől', missingRoomConfig: 'Hiányzó szobaazonosító' },
  ro: { priceFrom: 'De la', missingRoomConfig: 'Lipsește identificatorul camerei' },
  bg: { priceFrom: 'От', missingRoomConfig: 'Липсва идентификатор на стая' },
  hr: { priceFrom: 'Od', missingRoomConfig: 'Nedostaje identifikator sobe' },
  sl: { priceFrom: 'Od', missingRoomConfig: 'Manjka identifikator sobe' },
  da: { priceFrom: 'Fra', missingRoomConfig: 'Manglende værelses-id' },
  sv: { priceFrom: 'Från', missingRoomConfig: 'Saknat rum-id' },
  no: { priceFrom: 'Fra', missingRoomConfig: 'Manglende rom-id' },
  nb: { priceFrom: 'Fra', missingRoomConfig: 'Manglende rom-id' },
  nn: { priceFrom: 'Frå', missingRoomConfig: 'Manglende rom-id' },
  fi: { priceFrom: 'Alkaen', missingRoomConfig: 'Huoneen tunniste puuttuu' },
  el: { priceFrom: 'Από', missingRoomConfig: 'Λείπει το αναγνωριστικό δωματίου' },
  et: { priceFrom: 'Alates', missingRoomConfig: 'Toa tunnus puudub' },
  lv: { priceFrom: 'No', missingRoomConfig: 'Trūkst istabas identifikatora' },
  lt: { priceFrom: 'Nuo', missingRoomConfig: 'Trūksta kambario identifikatoriaus' },
  ga: { priceFrom: 'Ó', missingRoomConfig: 'Aitheantas seomra ar iarraidh' },
  ca: { priceFrom: 'Des de', missingRoomConfig: 'Falta l’identificador de l’habitació' },
};

const fr: BaseTranslationKeys = {
  checkin: 'Arrivée',
  checkout: 'Départ',
  guests: 'Personnes',
  book: 'Réserver',
  officialSite: 'Site officiel',
  otherSites: 'Autres sites',
  unavailable: 'Indisponible',
  noRateAvailable: 'Aucun tarif disponible pour ces dates.',
  missingToken: 'Token Directus manquant (data-token).',
  unknownError: 'Erreur inconnue.',
  invalidToken: 'Configuration invalide (token rejeté par le serveur).',
  serviceUnavailable: (s) => `Le service est temporairement indisponible (${s}).`,
  noData: 'Aucune donnée tarifaire retournée par le serveur.',
  decrease: 'Diminuer',
  increase: 'Augmenter',
  nightsSummary: (n, g) =>
    `${n} nuit${n > 1 ? 's' : ''} · ${g} personne${g > 1 ? 's' : ''}`,
};

const en: BaseTranslationKeys = {
  checkin: 'Check-in',
  checkout: 'Check-out',
  guests: 'Guests',
  book: 'Book',
  officialSite: 'Official site',
  otherSites: 'Other sites',
  unavailable: 'Unavailable',
  noRateAvailable: 'No rate available for these dates.',
  missingToken: 'Missing Directus token (data-token).',
  unknownError: 'Unknown error.',
  invalidToken: 'Invalid configuration (token rejected by server).',
  serviceUnavailable: (s) => `Service temporarily unavailable (${s}).`,
  noData: 'No pricing data returned by the server.',
  decrease: 'Decrease',
  increase: 'Increase',
  nightsSummary: (n, g) =>
    `${n} night${n > 1 ? 's' : ''} · ${g} guest${g > 1 ? 's' : ''}`,
};

const de: BaseTranslationKeys = {
  checkin: 'Anreise',
  checkout: 'Abreise',
  guests: 'Personen',
  book: 'Buchen',
  officialSite: 'Offizielle Seite',
  otherSites: 'Andere Seiten',
  unavailable: 'Nicht verfügbar',
  noRateAvailable: 'Kein Tarif für diese Daten verfügbar.',
  missingToken: 'Directus-Token fehlt (data-token).',
  unknownError: 'Unbekannter Fehler.',
  invalidToken: 'Ungültige Konfiguration (Token vom Server abgelehnt).',
  serviceUnavailable: (s) => `Dienst vorübergehend nicht verfügbar (${s}).`,
  noData: 'Keine Preisdaten vom Server zurückgegeben.',
  decrease: 'Verringern',
  increase: 'Erhöhen',
  nightsSummary: (n, g) =>
    `${n} ${n > 1 ? 'Nächte' : 'Nacht'} · ${g} ${g > 1 ? 'Personen' : 'Person'}`,
};

const es: BaseTranslationKeys = {
  checkin: 'Llegada',
  checkout: 'Salida',
  guests: 'Personas',
  book: 'Reservar',
  officialSite: 'Sitio oficial',
  otherSites: 'Otros sitios',
  unavailable: 'No disponible',
  noRateAvailable: 'No hay tarifa disponible para estas fechas.',
  missingToken: 'Token de Directus no proporcionado (data-token).',
  unknownError: 'Error desconocido.',
  invalidToken: 'Configuración inválida (token rechazado por el servidor).',
  serviceUnavailable: (s) => `Servicio temporalmente no disponible (${s}).`,
  noData: 'No se han devuelto datos de precios del servidor.',
  decrease: 'Disminuir',
  increase: 'Aumentar',
  nightsSummary: (n, g) =>
    `${n} noche${n > 1 ? 's' : ''} · ${g} persona${g > 1 ? 's' : ''}`,
};

const it: BaseTranslationKeys = {
  checkin: 'Arrivo',
  checkout: 'Partenza',
  guests: 'Persone',
  book: 'Prenota',
  officialSite: 'Sito ufficiale',
  otherSites: 'Altri siti',
  unavailable: 'Non disponibile',
  noRateAvailable: 'Nessuna tariffa disponibile per queste date.',
  missingToken: 'Token Directus mancante (data-token).',
  unknownError: 'Errore sconosciuto.',
  invalidToken: 'Configurazione non valida (token rifiutato dal server).',
  serviceUnavailable: (s) => `Servizio temporaneamente non disponibile (${s}).`,
  noData: 'Nessun dato tariffario restituito dal server.',
  decrease: 'Diminuire',
  increase: 'Aumentare',
  nightsSummary: (n, g) =>
    `${n} nott${n > 1 ? 'i' : 'e'} · ${g} person${g > 1 ? 'e' : 'a'}`,
};

const nl: BaseTranslationKeys = {
  checkin: 'Aankomst',
  checkout: 'Vertrek',
  guests: 'Personen',
  book: 'Boeken',
  officialSite: 'Officiële site',
  otherSites: 'Andere sites',
  unavailable: 'Niet beschikbaar',
  noRateAvailable: 'Geen tarief beschikbaar voor deze data.',
  missingToken: 'Directus-token ontbreekt (data-token).',
  unknownError: 'Onbekende fout.',
  invalidToken: 'Ongeldige configuratie (token geweigerd door server).',
  serviceUnavailable: (s) => `Service tijdelijk niet beschikbaar (${s}).`,
  noData: 'Geen prijsgegevens ontvangen van de server.',
  decrease: 'Verlagen',
  increase: 'Verhogen',
  nightsSummary: (n, g) =>
    `${n} nacht${n > 1 ? 'en' : ''} · ${g} ${g > 1 ? 'personen' : 'persoon'}`,
};

const pt: BaseTranslationKeys = {
  checkin: 'Chegada',
  checkout: 'Saída',
  guests: 'Pessoas',
  book: 'Reservar',
  officialSite: 'Site oficial',
  otherSites: 'Outros sites',
  unavailable: 'Indisponível',
  noRateAvailable: 'Nenhuma tarifa disponível para estas datas.',
  missingToken: 'Token Directus em falta (data-token).',
  unknownError: 'Erro desconhecido.',
  invalidToken: 'Configuração inválida (token rejeitado pelo servidor).',
  serviceUnavailable: (s) => `Serviço temporariamente indisponível (${s}).`,
  noData: 'Nenhum dado tarifário retornado pelo servidor.',
  decrease: 'Diminuir',
  increase: 'Aumentar',
  nightsSummary: (n, g) =>
    `${n} noite${n > 1 ? 's' : ''} · ${g} pessoa${g > 1 ? 's' : ''}`,
};

const pl: BaseTranslationKeys = {
  checkin: 'Przyjazd',
  checkout: 'Wyjazd',
  guests: 'Osoby',
  book: 'Rezerwuj',
  officialSite: 'Strona oficjalna',
  otherSites: 'Inne strony',
  unavailable: 'Niedostępne',
  noRateAvailable: 'Brak dostępnych cen na te daty.',
  missingToken: 'Brak tokenu Directus (data-token).',
  unknownError: 'Nieznany błąd.',
  invalidToken: 'Nieprawidłowa konfiguracja (token odrzucony przez serwer).',
  serviceUnavailable: (s) => `Usługa tymczasowo niedostępna (${s}).`,
  noData: 'Serwer nie zwrócił danych cenowych.',
  decrease: 'Zmniejsz',
  increase: 'Zwiększ',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'noc' : n < 5 ? 'noce' : 'nocy';
    const gLabel = g === 1 ? 'osoba' : g < 5 ? 'osoby' : 'osób';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const cs: BaseTranslationKeys = {
  checkin: 'Příjezd',
  checkout: 'Odjezd',
  guests: 'Osoby',
  book: 'Rezervovat',
  officialSite: 'Oficiální stránka',
  otherSites: 'Ostatní stránky',
  unavailable: 'Nedostupné',
  noRateAvailable: 'Pro tyto termíny není k dispozici žádná sazba.',
  missingToken: 'Chybí token Directus (data-token).',
  unknownError: 'Neznámá chyba.',
  invalidToken: 'Neplatná konfigurace (token odmítnut serverem).',
  serviceUnavailable: (s) => `Služba dočasně nedostupná (${s}).`,
  noData: 'Server nevrátil žádné cenové údaje.',
  decrease: 'Snížit',
  increase: 'Zvýšit',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'noc' : n < 5 ? 'noci' : 'nocí';
    const gLabel = g === 1 ? 'osoba' : g < 5 ? 'osoby' : 'osob';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const sk: BaseTranslationKeys = {
  checkin: 'Príchod',
  checkout: 'Odchod',
  guests: 'Osoby',
  book: 'Rezervovať',
  officialSite: 'Oficiálna stránka',
  otherSites: 'Ostatné stránky',
  unavailable: 'Nedostupné',
  noRateAvailable: 'Pre tieto dátumy nie je k dispozícii žiadna sadzba.',
  missingToken: 'Chýba token Directus (data-token).',
  unknownError: 'Neznáma chyba.',
  invalidToken: 'Neplatná konfigurácia (token odmietnutý serverom).',
  serviceUnavailable: (s) => `Služba dočasne nedostupná (${s}).`,
  noData: 'Server nevrátil žiadne cenové údaje.',
  decrease: 'Znížiť',
  increase: 'Zvýšiť',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'noc' : n < 5 ? 'noci' : 'nocí';
    const gLabel = g === 1 ? 'osoba' : g < 5 ? 'osoby' : 'osôb';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const hu: BaseTranslationKeys = {
  checkin: 'Érkezés',
  checkout: 'Távozás',
  guests: 'Személyek',
  book: 'Foglalás',
  officialSite: 'Hivatalos oldal',
  otherSites: 'Egyéb oldalak',
  unavailable: 'Nem elérhető',
  noRateAvailable: 'Nincs elérhető ár ezekre a dátumokra.',
  missingToken: 'Hiányzó Directus token (data-token).',
  unknownError: 'Ismeretlen hiba.',
  invalidToken: 'Érvénytelen konfiguráció (a szerver elutasította a tokent).',
  serviceUnavailable: (s) => `A szolgáltatás átmenetileg nem elérhető (${s}).`,
  noData: 'A szerver nem adott vissza árinformációt.',
  decrease: 'Csökkentés',
  increase: 'Növelés',
  nightsSummary: (n, g) =>
    `${n} éjszaka · ${g} személy`,
};

const ro: BaseTranslationKeys = {
  checkin: 'Sosire',
  checkout: 'Plecare',
  guests: 'Persoane',
  book: 'Rezervă',
  officialSite: 'Site oficial',
  otherSites: 'Alte site-uri',
  unavailable: 'Indisponibil',
  noRateAvailable: 'Niciun tarif disponibil pentru aceste date.',
  missingToken: 'Token Directus lipsă (data-token).',
  unknownError: 'Eroare necunoscută.',
  invalidToken: 'Configurație invalidă (token respins de server).',
  serviceUnavailable: (s) => `Serviciu temporar indisponibil (${s}).`,
  noData: 'Serverul nu a returnat date tarifare.',
  decrease: 'Micșorează',
  increase: 'Mărește',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'noapte' : 'nopți';
    const gLabel = n === 1 ? 'persoană' : 'persoane';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const bg: BaseTranslationKeys = {
  checkin: 'Настаняване',
  checkout: 'Напускане',
  guests: 'Гости',
  book: 'Резервирай',
  officialSite: 'Официален сайт',
  otherSites: 'Други сайтове',
  unavailable: 'Недостъпно',
  noRateAvailable: 'Няма налична цена за тези дати.',
  missingToken: 'Липсва Directus токен (data-token).',
  unknownError: 'Неизвестна грешка.',
  invalidToken: 'Невалидна конфигурация (токенът е отхвърлен от сървъра).',
  serviceUnavailable: (s) => `Услугата временно не е налична (${s}).`,
  noData: 'Сървърът не върна ценови данни.',
  decrease: 'Намали',
  increase: 'Увеличи',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'нощ' : 'нощи';
    const gLabel = g === 1 ? 'гост' : 'гости';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const hr: BaseTranslationKeys = {
  checkin: 'Dolazak',
  checkout: 'Odlazak',
  guests: 'Osobe',
  book: 'Rezerviraj',
  officialSite: 'Službena stranica',
  otherSites: 'Ostale stranice',
  unavailable: 'Nedostupno',
  noRateAvailable: 'Nema dostupne cijene za ove datume.',
  missingToken: 'Nedostaje Directus token (data-token).',
  unknownError: 'Nepoznata greška.',
  invalidToken: 'Neispravna konfiguracija (token odbijen od servera).',
  serviceUnavailable: (s) => `Usluga privremeno nedostupna (${s}).`,
  noData: 'Server nije vratio podatke o cijenama.',
  decrease: 'Smanji',
  increase: 'Povećaj',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'noć' : n < 5 ? 'noći' : 'noći';
    const gLabel = g === 1 ? 'osoba' : g < 5 ? 'osobe' : 'osoba';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const sl: BaseTranslationKeys = {
  checkin: 'Prihod',
  checkout: 'Odhod',
  guests: 'Osebe',
  book: 'Rezerviraj',
  officialSite: 'Uradna stran',
  otherSites: 'Druge strani',
  unavailable: 'Ni na voljo',
  noRateAvailable: 'Za te datume ni na voljo nobena cena.',
  missingToken: 'Manjka Directus žeton (data-token).',
  unknownError: 'Neznana napaka.',
  invalidToken: 'Neveljavna konfiguracija (žeton zavrnjen s strani strežnika).',
  serviceUnavailable: (s) => `Storitev začasno ni na voljo (${s}).`,
  noData: 'Strežnik ni vrnil cenovnih podatkov.',
  decrease: 'Zmanjšaj',
  increase: 'Povečaj',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'noč' : n === 2 ? 'noči' : n < 5 ? 'noči' : 'noči';
    const gLabel = g === 1 ? 'oseba' : g === 2 ? 'osebi' : g < 5 ? 'osebe' : 'oseb';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const da: BaseTranslationKeys = {
  checkin: 'Ankomst',
  checkout: 'Afrejse',
  guests: 'Personer',
  book: 'Reservér',
  officialSite: 'Officiel side',
  otherSites: 'Andre sider',
  unavailable: 'Ikke tilgængelig',
  noRateAvailable: 'Ingen pris tilgængelig for disse datoer.',
  missingToken: 'Manglende Directus-token (data-token).',
  unknownError: 'Ukendt fejl.',
  invalidToken: 'Ugyldig konfiguration (token afvist af serveren).',
  serviceUnavailable: (s) => `Tjenesten er midlertidigt utilgængelig (${s}).`,
  noData: 'Serveren returnerede ingen prisdata.',
  decrease: 'Formindsk',
  increase: 'Forøg',
  nightsSummary: (n, g) =>
    `${n} ${n > 1 ? 'nætter' : 'nat'} · ${g} ${g > 1 ? 'personer' : 'person'}`,
};

const sv: BaseTranslationKeys = {
  checkin: 'Incheckning',
  checkout: 'Utcheckning',
  guests: 'Personer',
  book: 'Boka',
  officialSite: 'Officiell sida',
  otherSites: 'Andra sidor',
  unavailable: 'Ej tillgängligt',
  noRateAvailable: 'Inget pris tillgängligt för dessa datum.',
  missingToken: 'Directus-token saknas (data-token).',
  unknownError: 'Okänt fel.',
  invalidToken: 'Ogiltig konfiguration (token avvisad av servern).',
  serviceUnavailable: (s) => `Tjänsten är tillfälligt otillgänglig (${s}).`,
  noData: 'Servern returnerade ingen prisdata.',
  decrease: 'Minska',
  increase: 'Öka',
  nightsSummary: (n, g) =>
    `${n} ${n > 1 ? 'nätter' : 'natt'} · ${g} ${g > 1 ? 'personer' : 'person'}`,
};

const no: BaseTranslationKeys = {
  checkin: 'Ankomst',
  checkout: 'Avreise',
  guests: 'Personer',
  book: 'Bestill',
  officialSite: 'Offisiell side',
  otherSites: 'Andre sider',
  unavailable: 'Ikke tilgjengelig',
  noRateAvailable: 'Ingen pris tilgjengelig for disse datoene.',
  missingToken: 'Manglende Directus-token (data-token).',
  unknownError: 'Ukjent feil.',
  invalidToken: 'Ugyldig konfigurasjon (token avvist av serveren).',
  serviceUnavailable: (s) => `Tjenesten er midlertidig utilgjengelig (${s}).`,
  noData: 'Serveren returnerte ingen prisdata.',
  decrease: 'Reduser',
  increase: 'Øk',
  nightsSummary: (n, g) =>
    `${n} ${n > 1 ? 'netter' : 'natt'} · ${g} ${g > 1 ? 'personer' : 'person'}`,
};

const fi: BaseTranslationKeys = {
  checkin: 'Saapuminen',
  checkout: 'Lähtö',
  guests: 'Henkilöt',
  book: 'Varaa',
  officialSite: 'Virallinen sivusto',
  otherSites: 'Muut sivustot',
  unavailable: 'Ei saatavilla',
  noRateAvailable: 'Näille päiville ei ole saatavilla hintaa.',
  missingToken: 'Directus-tunnus puuttuu (data-token).',
  unknownError: 'Tuntematon virhe.',
  invalidToken: 'Virheellinen määritys (palvelin hylkäsi tunnuksen).',
  serviceUnavailable: (s) => `Palvelu tilapäisesti poissa käytöstä (${s}).`,
  noData: 'Palvelin ei palauttanut hintatietoja.',
  decrease: 'Vähennä',
  increase: 'Lisää',
  nightsSummary: (n, g) =>
    `${n} ${n > 1 ? 'yötä' : 'yö'} · ${g} ${g > 1 ? 'henkilöä' : 'henkilö'}`,
};

const el: BaseTranslationKeys = {
  checkin: 'Άφιξη',
  checkout: 'Αναχώρηση',
  guests: 'Άτομα',
  book: 'Κράτηση',
  officialSite: 'Επίσημη σελίδα',
  otherSites: 'Άλλες σελίδες',
  unavailable: 'Μη διαθέσιμο',
  noRateAvailable: 'Δεν υπάρχει διαθέσιμη τιμή για αυτές τις ημερομηνίες.',
  missingToken: 'Λείπει το Directus token (data-token).',
  unknownError: 'Άγνωστο σφάλμα.',
  invalidToken: 'Μη έγκυρη διαμόρφωση (το token απορρίφθηκε από τον διακομιστή).',
  serviceUnavailable: (s) => `Η υπηρεσία είναι προσωρινά μη διαθέσιμη (${s}).`,
  noData: 'Ο διακομιστής δεν επέστρεψε δεδομένα τιμών.',
  decrease: 'Μείωση',
  increase: 'Αύξηση',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'διανυκτέρευση' : 'διανυκτερεύσεις';
    const gLabel = g === 1 ? 'άτομο' : 'άτομα';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const et: BaseTranslationKeys = {
  checkin: 'Saabumine',
  checkout: 'Lahkumine',
  guests: 'Isikud',
  book: 'Broneeri',
  officialSite: 'Ametlik leht',
  otherSites: 'Teised lehed',
  unavailable: 'Pole saadaval',
  noRateAvailable: 'Nendele kuupäevadele pole hinda saadaval.',
  missingToken: 'Directus tunnus puudub (data-token).',
  unknownError: 'Tundmatu viga.',
  invalidToken: 'Vigane seadistus (server lükkas tunnuse tagasi).',
  serviceUnavailable: (s) => `Teenus ajutiselt kättesaamatu (${s}).`,
  noData: 'Server ei tagastanud hinnaandmeid.',
  decrease: 'Vähenda',
  increase: 'Suurenda',
  nightsSummary: (n, g) =>
    `${n} öö${n > 1 ? 'd' : ''} · ${g} isik${g > 1 ? 'ut' : ''}`,
};

const lv: BaseTranslationKeys = {
  checkin: 'Ierašanās',
  checkout: 'Izrakstīšanās',
  guests: 'Personas',
  book: 'Rezervēt',
  officialSite: 'Oficiālā vietne',
  otherSites: 'Citas vietnes',
  unavailable: 'Nav pieejams',
  noRateAvailable: 'Šiem datumiem nav pieejama cena.',
  missingToken: 'Trūkst Directus pilnvaras (data-token).',
  unknownError: 'Nezināma kļūda.',
  invalidToken: 'Nederīga konfigurācija (serveris noraidīja pilnvaru).',
  serviceUnavailable: (s) => `Pakalpojums īslaicīgi nepieejams (${s}).`,
  noData: 'Serveris neatgrieza cenu datus.',
  decrease: 'Samazināt',
  increase: 'Palielināt',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'nakts' : 'naktis';
    const gLabel = g === 1 ? 'persona' : 'personas';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const lt: BaseTranslationKeys = {
  checkin: 'Atvykimas',
  checkout: 'Išvykimas',
  guests: 'Asmenys',
  book: 'Rezervuoti',
  officialSite: 'Oficialus puslapis',
  otherSites: 'Kiti puslapiai',
  unavailable: 'Nepasiekiama',
  noRateAvailable: 'Šioms datoms nėra galimų kainų.',
  missingToken: 'Trūksta Directus prieigos rakto (data-token).',
  unknownError: 'Nežinoma klaida.',
  invalidToken: 'Neteisinga konfigūracija (serveris atmetė prieigos raktą).',
  serviceUnavailable: (s) => `Paslauga laikinai nepasiekiama (${s}).`,
  noData: 'Serveris negrąžino kainų duomenų.',
  decrease: 'Sumažinti',
  increase: 'Padidinti',
  nightsSummary: (n, g) => {
    const nLabel = n === 1 ? 'naktis' : n < 10 ? 'naktys' : 'naktų';
    const gLabel = g === 1 ? 'asmuo' : g < 10 ? 'asmenys' : 'asmenų';
    return `${n} ${nLabel} · ${g} ${gLabel}`;
  },
};

const ga: BaseTranslationKeys = {
  checkin: 'Seiceáil isteach',
  checkout: 'Seiceáil amach',
  guests: 'Aíonna',
  book: 'Cuir in áirithe',
  officialSite: 'Suíomh oifigiúil',
  otherSites: 'Suímh eile',
  unavailable: 'Níl fáil air',
  noRateAvailable: 'Níl aon ráta ar fáil do na dátaí seo.',
  missingToken: 'Comhartha Directus ar iarraidh (data-token).',
  unknownError: 'Earráid anaithnid.',
  invalidToken: 'Cumraíocht neamhbhailí (diúltaíodh an comhartha ag an bhfreastalaí).',
  serviceUnavailable: (s) => `Seirbhís gan fáil go sealadach (${s}).`,
  noData: 'Níor sheol an freastalaí sonraí praghsála ar ais.',
  decrease: 'Laghdaigh',
  increase: 'Méadaigh',
  nightsSummary: (n, g) =>
    `${n} oíche${n > 1 ? 'anta' : ''} · ${g} ${g > 1 ? 'aíonna' : 'aoi'}`,
};

const ca: BaseTranslationKeys = {
  checkin: 'Arribada',
  checkout: 'Sortida',
  guests: 'Persones',
  book: 'Reservar',
  officialSite: 'Lloc oficial',
  otherSites: 'Altres llocs',
  unavailable: 'No disponible',
  noRateAvailable: 'Cap tarifa disponible per a aquestes dates.',
  missingToken: 'Falta el token Directus (data-token).',
  unknownError: 'Error desconegut.',
  invalidToken: 'Configuració no vàlida (token rebutjat pel servidor).',
  serviceUnavailable: (s) => `Servei temporalment no disponible (${s}).`,
  noData: 'El servidor no ha retornat dades de preus.',
  decrease: 'Disminuir',
  increase: 'Augmentar',
  nightsSummary: (n, g) =>
    `${n} nit${n > 1 ? 's' : ''} · ${g} persona${g > 1 ? 'es' : ''}`,
};

const translations: Record<string, BaseTranslationKeys> = {
  fr, en, de, es, it, nl, pt, pl, cs, sk, hu, ro,
  bg, hr, sl, da, sv, no, nb: no, nn: no, fi, el,
  et, lv, lt, ga, ca,
};

export function t(locale: string): TranslationKeys {
  const lang = locale.split('-')[0].toLowerCase();
  const base = translations[lang] ?? fr;
  const room = ROOM_WIDGET_STRINGS[lang] ?? ROOM_WIDGET_STRINGS.fr;
  return { ...base, ...room };
}
